import type { ComponentProps, FocusEvent, KeyboardEvent } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { useSelectContext } from "./SelectContext";

export type SelectTriggerButtonProps = ComponentProps<"button"> & {
  openOnFocus?: boolean;
  asChild?: boolean;
};

/** The trigger control itself; `Select.Trigger` places it (and, in multi mode, the badges under it). */
export const SelectTriggerButton = ({
  className,
  children,
  openOnFocus = false,
  asChild = false,
  disabled,
  onFocus,
  onKeyDown,
  ...props
}: SelectTriggerButtonProps) => {
  const { selectedValues, setOpen, disabled: rootDisabled, suppressFocusOpen } = useSelectContext();
  const showsPlaceholder = selectedValues.length === 0;

  const handleFocus = (event: FocusEvent<HTMLButtonElement>) => {
    onFocus?.(event);
    if (suppressFocusOpen.current) {
      suppressFocusOpen.current = false;
      return;
    }
    if (openOnFocus) setOpen(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    // Opening on keydown (as Radix Select does) also stops the button's native click from toggling it shut again.
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
    }
  };

  // The Popover trigger is itself a Slot, so a custom child receives the trigger wiring directly. It
  // keeps its native button role: a combobox can't be named by its content, and a custom trigger is
  // named by its own visible text.
  if (asChild) {
    return (
      <PopoverPrimitive.Trigger
        asChild
        aria-haspopup="listbox"
        data-slot="select-trigger"
        disabled={disabled || rootDisabled}
        className={className}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </PopoverPrimitive.Trigger>
    );
  }

  return (
    <PopoverPrimitive.Trigger asChild>
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        data-slot="select-trigger"
        data-placeholder={showsPlaceholder ? "" : undefined}
        disabled={disabled || rootDisabled}
        className={cn(
          "flex h-8 items-center justify-between gap-2 rounded-[10px] border border-input bg-background px-3 py-2 text-sm whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/20 data-[placeholder]:text-muted-foreground dark:bg-input/30 dark:hover:bg-input/50 dark:aria-[invalid=true]:ring-destructive/40 [&>[data-slot=select-value]]:flex [&>[data-slot=select-value]]:items-center [&>[data-slot=select-value]]:gap-2 [&_span]:w-full [&_span]:truncate [&_span]:text-left [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
          className
        )}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
        <ChevronDownIcon className="size-4 opacity-50" />
      </button>
    </PopoverPrimitive.Trigger>
  );
};
